import { useEffect, useState } from "react";
import { ChevronLeft, ListFilter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  DATE_PRESET_LABELS,
  filterChipLabel,
} from "@/modules/shared/dataGrid/gridFilters";
import type {
  DatePreset,
  GridColumn,
  GridFilter,
} from "@/modules/shared/dataGrid/gridTypes";

export const emptyFilterFor = <Row,>(column: GridColumn<Row>): GridFilter => {
  switch (column.type) {
    case "select":
      return { columnId: column.id, kind: "select", values: [] };
    case "date":
      return { columnId: column.id, kind: "date", preset: "this_month" };
    case "number":
    case "money":
      return { columnId: column.id, kind: "range", min: null, max: null };
    case "boolean":
      return { columnId: column.id, kind: "boolean", value: true };
    default:
      return { columnId: column.id, kind: "text", contains: "" };
  }
};

export const isComplete = (filter: GridFilter) => {
  switch (filter.kind) {
    case "text":
      return filter.contains.trim().length > 0;
    case "select":
      return filter.values.length > 0;
    case "date":
      return filter.preset !== "custom" || Boolean(filter.from || filter.to);
    case "range":
      return filter.min != null || filter.max != null;
    default:
      return true;
  }
};

const numberOrNull = (value: string) =>
  value.trim() === "" || !Number.isFinite(Number(value)) ? null : Number(value);

/** Editor for one filter, by column type (also "is empty"). */
export const FilterEditor = <Row,>({
  column,
  filter,
  onChange,
}: {
  column: GridColumn<Row>;
  filter: GridFilter;
  onChange: (filter: GridFilter) => void;
}) => {
  const emptyChoice =
    filter.kind === "empty" ? (filter.empty ? "empty" : "not_empty") : null;

  const editor = (() => {
    switch (filter.kind) {
      case "text":
        return (
          <Input
            autoFocus
            placeholder={`${column.label} contains…`}
            value={filter.contains}
            onChange={(e) => onChange({ ...filter, contains: e.target.value })}
          />
        );
      case "select":
        return (
          <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
            {(column.options ?? []).map((option) => (
              <li key={option.value}>
                <label className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted">
                  <Checkbox
                    checked={filter.values.includes(option.value)}
                    onCheckedChange={(checked) =>
                      onChange({
                        ...filter,
                        values:
                          checked === true
                            ? [...filter.values, option.value]
                            : filter.values.filter((v) => v !== option.value),
                      })
                    }
                  />
                  {option.label}
                </label>
              </li>
            ))}
          </ul>
        );
      case "date":
        return (
          <div className="flex flex-col gap-1">
            {(Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map((preset) => (
              <Button
                key={preset}
                type="button"
                size="sm"
                variant={filter.preset === preset ? "secondary" : "ghost"}
                className="h-7 justify-start text-sm"
                onClick={() => onChange({ ...filter, preset })}
              >
                {DATE_PRESET_LABELS[preset]}
              </Button>
            ))}
            {filter.preset === "custom" ? (
              <div className="mt-1 grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  aria-label="From"
                  value={filter.from ?? ""}
                  onChange={(e) =>
                    onChange({ ...filter, from: e.target.value || null })
                  }
                />
                <Input
                  type="date"
                  aria-label="To"
                  value={filter.to ?? ""}
                  onChange={(e) =>
                    onChange({ ...filter, to: e.target.value || null })
                  }
                />
              </div>
            ) : null}
          </div>
        );
      case "range":
        return (
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={filter.min ?? ""}
              onChange={(e) =>
                onChange({ ...filter, min: numberOrNull(e.target.value) })
              }
            />
            <Input
              type="number"
              placeholder="Max"
              value={filter.max ?? ""}
              onChange={(e) =>
                onChange({ ...filter, max: numberOrNull(e.target.value) })
              }
            />
          </div>
        );
      case "boolean":
        return (
          <div className="flex gap-1">
            {[true, false].map((value) => (
              <Button
                key={String(value)}
                type="button"
                size="sm"
                variant={filter.value === value ? "secondary" : "ghost"}
                className="h-7 flex-1"
                onClick={() => onChange({ ...filter, value })}
              >
                {value ? "Yes" : "No"}
              </Button>
            ))}
          </div>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="flex flex-col gap-2">
      {editor}
      {column.type !== "boolean" ? (
        <div className="flex gap-1 border-t pt-2">
          {(["empty", "not_empty"] as const).map((choice) => (
            <Button
              key={choice}
              type="button"
              size="sm"
              variant={emptyChoice === choice ? "secondary" : "ghost"}
              className="h-7 flex-1 text-xs"
              onClick={() =>
                onChange(
                  emptyChoice === choice
                    ? emptyFilterFor(column)
                    : {
                        columnId: column.id,
                        kind: "empty",
                        empty: choice === "empty",
                      },
                )
              }
            >
              {choice === "empty" ? "Is empty" : "Is not empty"}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * "Filter" button: pick a column, set its condition. One filter per column;
 * an existing one opens for editing.
 */
export const GridFilterMenu = <Row,>({
  columns,
  filters,
  onChange,
  editColumnId,
  onEditColumnIdChange,
}: {
  columns: GridColumn<Row>[];
  filters: GridFilter[];
  onChange: (filters: GridFilter[]) => void;
  /** Column being edited (chips open the menu on their column). */
  editColumnId: string | null;
  onEditColumnIdChange: (id: string | null) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<GridFilter | null>(null);
  const filterable = columns.filter((c) => c.filterable);
  const column = filterable.find(
    (c) => c.id === (draft?.columnId ?? editColumnId),
  );

  const startColumn = (id: string) => {
    const target = filterable.find((c) => c.id === id);
    if (!target) return;
    setDraft(filters.find((f) => f.columnId === id) ?? emptyFilterFor(target));
    onEditColumnIdChange(id);
  };

  const close = () => {
    setOpen(false);
    setDraft(null);
    onEditColumnIdChange(null);
  };

  const apply = () => {
    if (!draft) return;
    const rest = filters.filter((f) => f.columnId !== draft.columnId);
    onChange(isComplete(draft) ? [...rest, draft] : rest);
    close();
  };

  // A chip was clicked: open on its column.
  useEffect(() => {
    if (!editColumnId || draft?.columnId === editColumnId) return;
    const target = filterable.find((c) => c.id === editColumnId);
    if (!target) return;
    setDraft(
      filters.find((f) => f.columnId === editColumnId) ??
        emptyFilterFor(target),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editColumnId]);

  if (!filterable.length) return null;

  return (
    <Popover
      open={open || editColumnId != null}
      onOpenChange={(next) => {
        if (next) {
          setOpen(true);
          if (editColumnId) startColumn(editColumnId);
        } else close();
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8">
          <ListFilter className="size-4" />
          Filter
          {filters.length ? (
            <span className="rounded-full bg-primary px-1.5 text-2xs text-primary-foreground">
              {filters.length}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        {column && draft ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-1"
                aria-label="Back to columns"
                onClick={() => {
                  setDraft(null);
                  onEditColumnIdChange(null);
                }}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-sm font-medium">{column.label}</span>
            </div>
            <FilterEditor column={column} filter={draft} onChange={setDraft} />
            <div className="flex justify-end gap-1 border-t pt-2">
              {filters.some((f) => f.columnId === column.id) ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => {
                    onChange(filters.filter((f) => f.columnId !== column.id));
                    close();
                  }}
                >
                  Remove
                </Button>
              ) : null}
              <Button type="button" size="sm" className="h-7" onClick={apply}>
                Apply
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <p className="px-1.5 pb-1.5 text-xs font-medium text-muted-foreground">
              Filter by
            </p>
            {filterable.map((option) => (
              <Button
                key={option.id}
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 justify-start",
                  filters.some((f) => f.columnId === option.id) &&
                    "font-semibold",
                )}
                onClick={() => startColumn(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Active filters as chips: click to edit, × to remove, "Clear all". */
export const GridFilterChips = <Row,>({
  columns,
  filters,
  onChange,
  onEdit,
}: {
  columns: GridColumn<Row>[];
  filters: GridFilter[];
  onChange: (filters: GridFilter[]) => void;
  onEdit: (columnId: string) => void;
}) => {
  const active = filters.filter((f) =>
    columns.some((c) => c.id === f.columnId),
  );
  if (!active.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {active.map((filter) => {
        const column = columns.find((c) => c.id === filter.columnId)!;
        return (
          <span
            key={filter.columnId}
            className="inline-flex items-center gap-1 rounded-full border bg-primary/5 py-0.5 pl-2.5 pr-1 text-xs"
          >
            <button
              type="button"
              className="max-w-64 truncate hover:underline"
              onClick={() => onEdit(filter.columnId)}
            >
              {filterChipLabel(filter, column)}
            </button>
            <button
              type="button"
              className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={`Remove ${column.label} filter`}
              onClick={() =>
                onChange(filters.filter((f) => f.columnId !== filter.columnId))
              }
            >
              <X className="size-3" />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        onClick={() => onChange([])}
      >
        Clear all
      </button>
    </div>
  );
};
